import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Logo from "@/components/Logo";
import { FullPageLoader } from "@/components/Loader";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { useAffiliateAuth } from "@/lib/affiliateAuth";
import { titleCase } from "@/lib/format";
import { C } from "@/lib/brand";

const tabs = [
  { to: "/affiliates/dashboard", label: "OVERVIEW", end: true, testId: "aff-tab-overview" },
  { to: "/affiliates/dashboard/conversions", label: "CONVERSIONS", testId: "aff-tab-conversions" },
  { to: "/affiliates/dashboard/payouts", label: "PAYOUTS", testId: "aff-tab-payouts" },
  { to: "/affiliates/dashboard/assets", label: "ASSETS", testId: "aff-tab-assets" },
  { to: "/affiliates/dashboard/profile", label: "PROFILE", testId: "aff-tab-profile" },
];

export default function AffiliateShell() {
  const { profile, bootstrapped, logout } = useAffiliateAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (bootstrapped && !profile) navigate("/affiliates/sign-in", { replace: true });
  }, [bootstrapped, profile, navigate]);

  if (!bootstrapped) return <FullPageLoader />;
  if (!profile) return <FullPageLoader />;

  // Status gating — affiliates with an active dashboard see the
  // dashboard surface. The BE has shipped two value spellings for the
  // approved-and-onboarded state across iterations: "active" (older
  // collection rows) and "approved" (newer rows + the consumer profile
  // snapshot). We accept both so a member who just finished onboarding
  // — and whose BE status reads "approved" — doesn't get bounced back
  // to the pending screen. Anything else (pending, declined, suspended,
  // terminated) falls through to AffiliateStatusPage.
  const status = (profile.status || "active").toLowerCase();
  const isLive = status === "active" || status === "approved";
  if (!isLive) {
    return <AffiliateStatusPage status={status} email={profile.email} firstName={profile.firstName} onSignOut={logout} />;
  }

  return (
    <div data-testid="affiliate-shell" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 120, overflowX: "hidden" }}>
      <header
        data-testid="affiliate-header"
        style={{
          background: C.cream,
          borderBottom: `1px solid ${C.warmGray3}`,
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Link to="/affiliates/dashboard" data-testid="affiliate-logo-link" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <Logo size={20} />
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
              · AFFILIATE
            </span>
          </Link>
          <button
            type="button"
            data-testid="affiliate-signout"
            onClick={logout}
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: C.warmGray,
              background: "transparent",
              border: 0,
              cursor: "pointer",
            }}
          >
            SIGN OUT
          </button>
        </div>

        {/* Tab bar — horizontally scrollable on narrow screens */}
        <nav
          data-testid="affiliate-tabs"
          className="no-scrollbar"
          style={{
            display: "flex",
            gap: 4,
            overflowX: "auto",
            padding: "0 24px",
            borderTop: `1px solid ${C.warmGray3}`,
          }}
        >
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testId}
              style={({ isActive }) => ({
                padding: "14px 16px",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.2em",
                color: isActive ? C.ink : C.warmGray,
                textDecoration: "none",
                borderBottom: isActive ? `2px solid ${C.peachDeep}` : "2px solid transparent",
                whiteSpace: "nowrap",
                flexShrink: 0,
              })}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="page-enter">
        <Outlet context={{ profile }} />
      </main>
    </div>
  );
}

/* ----- Pending / Declined / Suspended status page ----- */
function AffiliateStatusPage({ status, email, firstName, onSignOut }) {
  const config = {
    pending: {
      eyebrow: "★ APPLICATION RECEIVED",
      before: "WE'RE",
      italic: "reviewing.",
      message: "We've received your application and are reviewing it personally. Most decisions land within 5 business days. We'll email you when a decision is made.",
      cta: null,
    },
    declined: {
      eyebrow: "★ APPLICATION DECISION",
      before: "NOT THIS",
      italic: "round.",
      message: "Thanks for applying to the VOYA9 affiliate program. After review, we don't have a fit at this time. You're welcome to apply again in 90 days as your audience grows.",
      cta: null,
    },
    suspended: {
      eyebrow: "★ ACCOUNT PAUSED",
      before: "ON",
      italic: "hold.",
      message: "Your affiliate account has been temporarily paused. Please reach out to affiliates@voya9.com to resolve.",
      cta: { href: "mailto:affiliates@voya9.com", label: "EMAIL THE TEAM →" },
    },
  };
  const c = config[status] || config.pending;

  return (
    <div data-testid={`affiliate-status-${status}`} style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: `1px solid ${C.warmGray3}` }}>
        <Link to="/affiliates"><Logo size={22} /></Link>
        <button
          type="button"
          data-testid="status-signout"
          onClick={onSignOut}
          style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.warmGray, background: "transparent", border: 0, cursor: "pointer" }}
        >
          SIGN OUT
        </button>
      </header>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "88px 24px 120px" }}>
        <Eyebrow color={C.peachDeep}>{c.eyebrow}</Eyebrow>
        <Headline before={c.before} italic={c.italic} size={64} className="mt-6" />
        {firstName && (
          <p style={{ marginTop: 18, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            Hello, {titleCase(firstName)}.
          </p>
        )}
        <p style={{ marginTop: 28, fontSize: 16, lineHeight: 1.65, color: C.ink }}>{c.message}</p>
        {email && (
          <p style={{ marginTop: 14, fontSize: 13, color: C.warmGray }}>
            We'll be in touch at <strong style={{ color: C.ink }}>{email}</strong>.
          </p>
        )}
        {c.cta && (
          <div style={{ marginTop: 36 }}>
            <a href={c.cta.href} style={{ textDecoration: "none" }}>
              <Button variant="primary" testId="status-cta-btn">{c.cta.label}</Button>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
